/**
 * ============================================================================
 * Slide deck on the projector — teach the point, push the item, reveal here
 * ============================================================================
 * Brian, 22 Sep 2026 rehearsal: "give the people in the room some context
 * about why they are answering the questions … the pushes can be buttons at
 * the bottom of the slide that is presenting the information and then the
 * reveal is live once the question is pushed … I should see the answers on
 * the screen without having to scroll."
 *
 * So: one slide per concept. The lead-in fills the slide. The items for that
 * concept are buttons along the foot. Pushing one puts the item and its live
 * tally at the TOP of the same slide; the lead-in folds away underneath.
 * Arrow keys move between slides. Nothing here changes what is stored — the
 * push endpoint is the same one the plain list uses.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import LiveResponses from './LiveResponses.jsx';

function shortStem(stem, max = 56) {
  const s = String(stem ?? '').replace(/\s+/g, ' ').trim();
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

export function SlideDeck({ deck, sessionId, questions, activeId, responses, busy, onPush }) {
  const byPosition = useMemo(() => {
    const m = new Map();
    for (const q of questions ?? []) m.set(q.position, q);
    return m;
  }, [questions]);

  // Which slide holds the live item, if any — used to land on it when the
  // presenter reloads mid-class, and to say where it is when it is elsewhere.
  const liveSlideIndex = useMemo(() => {
    if (!activeId) return -1;
    return deck.slides.findIndex((s) => s.items.some((p) => byPosition.get(p)?.id === activeId));
  }, [deck, activeId, byPosition]);

  // Full screen (Brian, 22 Sep: "seeing the sidebars eats up viewing material").
  // Two things at once: the app frame is hidden by a class on <body>, and the
  // browser is asked for real full screen. Either can fail on its own and the
  // other still helps, so they are not tied together.
  const [full, setFull] = useState(false);
  useEffect(() => {
    document.body.classList.toggle('presenting', full);
    return () => document.body.classList.remove('presenting');
  }, [full]);
  useEffect(() => {
    const onChange = () => { if (!document.fullscreenElement) setFull(false); };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  async function toggleFull() {
    const next = !full;
    setFull(next);
    try {
      if (next) await document.documentElement.requestFullscreen?.();
      else if (document.fullscreenElement) await document.exitFullscreen?.();
    } catch {
      // Blocked by the browser (or unsupported) — the frame is still hidden.
    }
  }

  const [index, setIndex] = useState(0);
  const [landed, setLanded] = useState(false);
  useEffect(() => {
    if (landed || questions === null) return;
    if (liveSlideIndex >= 0) setIndex(liveSlideIndex);
    setLanded(true);
  }, [landed, questions, liveSlideIndex]);

  const count = deck.slides.length;
  // How many lines of each slide have been revealed. The first line shows
  // with the title; each tap brings the next. Right arrow / Space reveal, and
  // move on once the slide is fully told. Left goes back a slide.
  const [shown, setShown] = useState({});
  const go = (delta) => setIndex((i) => Math.min(count - 1, Math.max(0, i + delta)));
  const shownOn = (i) => shown[i] ?? 1;
  const reveal = () => {
    const total = deck.slides[index].lines.length;
    if (shownOn(index) < total) setShown((m) => ({ ...m, [index]: shownOn(index) + 1 }));
    else go(1);
  };

  useEffect(() => {
    function onKey(e) {
      const tag = (e.target?.tagName ?? '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      // Space on a focused button is that button's click, not a reveal.
      if (e.key === ' ' && (tag === 'button' || tag === 'a' || tag === 'summary')) return;
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'PageDown') { e.preventDefault(); reveal(); }
      if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); go(-1); }
      if (e.key === 'Escape' && full) setFull(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count, index, shown, full]);

  const slide = deck.slides[index];
  const slideItems = slide.items.map((p) => byPosition.get(p)).filter(Boolean);
  const active = (questions ?? []).find((q) => q.id === activeId) ?? null;
  const liveHere = Boolean(active) && slideItems.some((q) => q.id === activeId);
  const visibleLines = slide.lines.slice(0, shownOn(index));
  const remaining = slide.lines.length - visibleLines.length;

  return (
    <section className="deck" aria-label={deck.title}>
      <div className="deck__bar">
        <button type="button" className="btn btn--quiet btn--auto" onClick={() => go(-1)} disabled={index === 0}>
          ◀ Back
        </button>
        <span className="deck__counter">
          {index + 1} / {count}
          <span className="muted"> · {deck.title}</span>
        </span>
        <button type="button" className="btn btn--quiet btn--auto" onClick={() => go(1)} disabled={index === count - 1}>
          Next ▶
        </button>
        <button type="button" className="btn btn--quiet btn--auto deck__roster" onClick={toggleFull}>
          {full ? 'Leave full screen' : 'Full screen'}
        </button>
        <Link className="btn btn--quiet btn--auto" to={`/sessions/${sessionId}/roster`}>
          Who’s here
        </Link>
      </div>

      <div className={liveHere ? 'deck__slide deck__slide--live' : 'deck__slide'}>
        <h2 className="deck__title">{slide.title}</h2>

        {liveHere ? (
          <>
            <div className="deck__live" aria-live="polite">
              <p className="deck__stem">{active.stem}</p>
              <LiveResponses responses={responses} />
            </div>
            <details className="deck__context">
              <summary>Context</summary>
              <ul className="deck__lines deck__lines--small">
                {slide.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </details>
          </>
        ) : (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div className="deck__body" onClick={remaining > 0 ? reveal : undefined}>
            <ul className="deck__lines">
              {visibleLines.map((line) => (
                <li key={line} className="deck__line">{line}</li>
              ))}
            </ul>
            {remaining > 0 && (
              <p className="deck__more muted">tap for the next point · {remaining} more</p>
            )}
          </div>
        )}

        {active && !liveHere && liveSlideIndex >= 0 && (
          <p className="deck__elsewhere">
            Item {active.position} is live on slide {liveSlideIndex + 1}.{' '}
            <button type="button" className="btn btn--quiet btn--auto" onClick={() => setIndex(liveSlideIndex)}>
              Go there
            </button>{' '}
            <button type="button" className="btn btn--quiet btn--auto" disabled={busy} onClick={() => onPush(null)}>
              Take down
            </button>
          </p>
        )}
      </div>

      <div className="deck__foot">
        {slideItems.map((q) => {
          const isLive = q.id === activeId;
          return (
            <button
              key={q.id}
              type="button"
              className={isLive ? 'btn btn--auto deck__push deck__push--live' : 'btn btn--primary btn--auto deck__push'}
              disabled={busy}
              onClick={() => onPush(isLive ? null : q.id)}
              title={q.stem}
            >
              {isLive ? 'Take down' : `Push ${q.position}`}
              <span className="deck__push-stem">
                {shortStem(q.stem)}
                {q.is_completion_check && <span className="badge badge--published"> final</span>}
                {q.pushed_at && !isLive && <span className="muted"> · asked</span>}
              </span>
            </button>
          );
        })}
        {slide.closing && (
          <Link className="btn btn--primary btn--auto" to={`/sessions/${sessionId}/roster`}>
            Open the roster →
          </Link>
        )}
        {slideItems.length === 0 && !slide.closing && <span className="muted">Nothing to push on this slide.</span>}
      </div>

      <p className="muted deck__hint">→ or Space: next point, then next slide. ←: back a slide. Answers are anonymous on this screen — no names, no IDs.</p>
    </section>
  );
}

export default SlideDeck;
